import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Users,
  HardDrive,
  Folder,
  Loader2,
  Check,
  X,
  Trash2,
  ShieldCheck,
  Shield,
  UserCheck,
  Ban,
  Pencil,
  Database,
} from "lucide-react";
import { toast } from "sonner";
import { DatabaseTab } from "@/components/admin/DatabaseTab";
import TopNav from "@/components/TopNav";
import { useAuth } from "@/context/AuthContext";
import {
  adminGetUsers,
  adminApproveUser,
  adminRejectUser,
  adminUpdateUser,
  adminDeleteUser,
  adminGetDevices,
  adminUpdateDeviceAccess,
  adminGetGroups,
  adminUpdateGroupOwner,
  formatApiError,
} from "@/lib/api";

const StatusBadge = ({ status }) => {
  const map = {
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    rejected: "bg-red-50 text-red-600 border-red-200",
  };
  const label = { approved: "Активний", pending: "Очікує", rejected: "Відхилено" };
  return (
    <span
      data-testid={`user-status-${status}`}
      className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${map[status] || map.approved}`}
    >
      {label[status] || status}
    </span>
  );
};

function UsersTab() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () =>
    adminGetUsers()
      .then(setUsers)
      .finally(() => setLoading(false));
  useEffect(() => {
    load();
  }, []);

  const act = async (fn, okMsg) => {
    try {
      await fn();
      toast.success(okMsg);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (loading)
    return (
      <div className="flex justify-center py-16 text-slate-400">
        <Loader2 className="animate-spin" />
      </div>
    );

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="admin-users-table">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-3">Користувач</th>
            <th className="px-4 py-3">Роль</th>
            <th className="px-4 py-3">Статус</th>
            <th className="px-4 py-3">Приладів</th>
            <th className="px-4 py-3 text-right">Дії</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {users.map((u) => {
            const isSelf = u.id === me?.id;
            return (
              <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{u.name || "—"}</div>
                  <div className="text-xs text-slate-500">{u.email}</div>
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1.5 text-slate-700">
                    {u.role === "admin" ? (
                      <ShieldCheck size={15} className="text-blue-600" />
                    ) : (
                      <Shield size={15} className="text-slate-400" />
                    )}
                    {u.role === "admin" ? "Адмін" : "Користувач"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={u.status} />
                </td>
                <td className="px-4 py-3 font-mono text-slate-700">{u.device_count}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {u.status !== "approved" && (
                      <button
                        data-testid={`admin-approve-${u.id}`}
                        onClick={() =>
                          act(() => adminApproveUser(u.id), "Користувача підтверджено")
                        }
                        title="Підтвердити"
                        className="rounded-md p-1.5 text-emerald-600 hover:bg-emerald-50"
                      >
                        <UserCheck size={16} />
                      </button>
                    )}
                    {u.status !== "rejected" && !isSelf && (
                      <button
                        data-testid={`admin-reject-${u.id}`}
                        onClick={() =>
                          act(() => adminRejectUser(u.id), "Доступ відхилено")
                        }
                        title="Відхилити доступ"
                        className="rounded-md p-1.5 text-amber-600 hover:bg-amber-50"
                      >
                        <Ban size={16} />
                      </button>
                    )}
                    {!isSelf && (
                      <button
                        data-testid={`admin-role-${u.id}`}
                        onClick={() =>
                          act(
                            () =>
                              adminUpdateUser(u.id, {
                                role: u.role === "admin" ? "user" : "admin",
                              }),
                            "Роль оновлено"
                          )
                        }
                        title={u.role === "admin" ? "Зробити користувачем" : "Зробити адміном"}
                        className="rounded-md p-1.5 text-blue-600 hover:bg-blue-50"
                      >
                        {u.role === "admin" ? <Shield size={16} /> : <ShieldCheck size={16} />}
                      </button>
                    )}
                    {!isSelf && (
                      <button
                        data-testid={`admin-delete-user-${u.id}`}
                        onClick={() => {
                          if (window.confirm(`Видалити користувача ${u.email}?`))
                            act(() => adminDeleteUser(u.id), "Користувача видалено");
                        }}
                        title="Видалити"
                        className="rounded-md p-1.5 text-red-500 hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DeviceAccessModal({ device, onClose, onSaved }) {
  const [owner, setOwner] = useState(device.owner_email || "");
  const [shared, setShared] = useState((device.shared_emails || []).join(", "));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const shared_emails = shared
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      await adminUpdateDeviceAccess(device.id, {
        owner_email: owner.trim(),
        shared_emails,
      });
      toast.success("Доступи оновлено");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative z-10 w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-lg"
        data-testid="device-access-modal"
      >
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-slate-900">
            Доступ · {device.name}
          </h2>
          <button onClick={onClose} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Власник (email)
            </label>
            <input
              data-testid="device-access-owner"
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              placeholder="owner@example.com"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Спільний доступ (emails через кому)
            </label>
            <textarea
              data-testid="device-access-shared"
              value={shared}
              onChange={(e) => setShared(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              placeholder="user1@example.com, user2@example.com"
            />
          </div>
          <button
            data-testid="device-access-save"
            onClick={save}
            disabled={saving}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {saving && <Loader2 size={16} className="animate-spin" />}
            Зберегти
          </button>
        </div>
      </div>
    </div>
  );
}

function DevicesTab() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () =>
    adminGetDevices()
      .then(setDevices)
      .finally(() => setLoading(false));
  useEffect(() => {
    load();
  }, []);

  if (loading)
    return (
      <div className="flex justify-center py-16 text-slate-400">
        <Loader2 className="animate-spin" />
      </div>
    );

  return (
    <>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="admin-devices-table">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Прилад</th>
              <th className="px-4 py-3">Група</th>
              <th className="px-4 py-3">Власник</th>
              <th className="px-4 py-3">Спільний доступ</th>
              <th className="px-4 py-3 text-right">Дії</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {devices.map((d) => (
              <tr key={d.id} data-testid={`admin-device-row-${d.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{d.name}</div>
                  <div className="font-mono text-xs text-slate-400">{d.mqtt_topic}</div>
                </td>
                <td className="px-4 py-3 text-slate-600">{d.group_name || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{d.owner_email || "—"}</td>
                <td className="px-4 py-3">
                  {d.shared_emails?.length ? (
                    <div className="flex flex-wrap gap-1">
                      {d.shared_emails.map((e) => (
                        <span key={e} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                          {e}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    data-testid={`admin-device-edit-${d.id}`}
                    onClick={() => setEditing(d)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                  >
                    <Pencil size={14} /> Доступ
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <DeviceAccessModal device={editing} onClose={() => setEditing(null)} onSaved={load} />
      )}
    </>
  );
}

function GroupsTab() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [emailVal, setEmailVal] = useState("");

  const load = () =>
    adminGetGroups()
      .then(setGroups)
      .finally(() => setLoading(false));
  useEffect(() => {
    load();
  }, []);

  const save = async (g) => {
    try {
      await adminUpdateGroupOwner(g.id, emailVal.trim());
      toast.success("Власника групи оновлено");
      setEditId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (loading)
    return (
      <div className="flex justify-center py-16 text-slate-400">
        <Loader2 className="animate-spin" />
      </div>
    );

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="admin-groups-table">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-3">Група</th>
            <th className="px-4 py-3">Власник</th>
            <th className="px-4 py-3 text-right">Дії</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {groups.map((g) => (
            <tr key={g.id} data-testid={`admin-group-row-${g.id}`} className="hover:bg-slate-50">
              <td className="px-4 py-3 font-medium text-slate-900">{g.name}</td>
              <td className="px-4 py-3 text-slate-600">
                {editId === g.id ? (
                  <input
                    data-testid={`admin-group-owner-input-${g.id}`}
                    autoFocus
                    value={emailVal}
                    onChange={(e) => setEmailVal(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") save(g);
                      if (e.key === "Escape") setEditId(null);
                    }}
                    className="rounded-md border border-blue-300 px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-blue-200"
                    placeholder="owner@example.com"
                  />
                ) : (
                  g.owner_email || "—"
                )}
              </td>
              <td className="px-4 py-3 text-right">
                {editId === g.id ? (
                  <div className="flex items-center justify-end gap-1">
                    <button
                      data-testid={`admin-group-owner-save-${g.id}`}
                      onClick={() => save(g)}
                      className="rounded-md p-1.5 text-emerald-600 hover:bg-emerald-50"
                    >
                      <Check size={16} />
                    </button>
                    <button
                      onClick={() => setEditId(null)}
                      className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <button
                    data-testid={`admin-group-edit-${g.id}`}
                    onClick={() => {
                      setEditId(g.id);
                      setEmailVal(g.owner_email || "");
                    }}
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                  >
                    <Pencil size={14} /> Власник
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminPanel() {
  const { user, ready } = useAuth();
  const [tab, setTab] = useState("users");

  if (ready && user && user.role !== "admin") return <Navigate to="/devices" replace />;

  const tabs = [
    { id: "users", label: "Користувачі", icon: Users },
    { id: "devices", label: "Прилади", icon: HardDrive },
    { id: "groups", label: "Групи", icon: Folder },
    { id: "database", label: "База даних", icon: Database },
  ];

  return (
    <div className="min-h-screen bg-slate-100 pt-16">
      <TopNav />
      <div className="mx-auto max-w-[1500px] px-6 py-8">
        <div className="mb-6">
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">
            Адмін-панель
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Керування користувачами та доступами до приладів і груп
          </p>
        </div>

        <div className="mb-6 flex gap-1 border-b border-slate-200">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
                tab === id
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              <Icon size={16} />
              {label}
            </button>
          ))}
        </div>

        {tab === "users" && <UsersTab />}
        {tab === "devices" && <DevicesTab />}
        {tab === "groups" && <GroupsTab />}
        {tab === "database" && <DatabaseTab />}
      </div>
    </div>
  );
}
